import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Mail, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const dest = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email.trim(), password);
    setLoading(false);
    if (ok) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 vp-login-grain"
           style={{ backgroundColor: "#1F1F24" }}>
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Via Permuta" className="w-14 h-14 object-contain bg-white rounded-xl p-1.5" />
          <div>
            <div className="text-white font-display font-bold text-2xl leading-tight">Via Permuta</div>
            <div className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Repositório RP</div>
          </div>
        </div>

        <div className="max-w-lg">
          <h2 className="text-white font-display font-bold text-4xl xl:text-5xl leading-tight">
            Sua central segura de <span style={{ color: "#FF8A00" }}>credenciais corporativas</span>.
          </h2>
          <p className="text-gray-400 mt-5 leading-relaxed">
            Armazene, organize e consulte senhas, tokens, APIs e ativos digitais com criptografia, auditoria
            e controle de expirações — tudo em um único repositório interno da Via Permuta.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <Feature icon={<ShieldCheck className="w-4 h-4" />} title="Criptografia AES" text="Tokens cifrados no banco" />
            <Feature icon={<Lock className="w-4 h-4" />} title="Acesso JWT" text="Autenticação segura" />
          </div>
        </div>

        <div className="text-xs text-gray-500">© {new Date().getFullYear()} Via Permuta. Uso interno.</div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F5F5F7]">
        <div className="w-full max-w-md bg-white border border-gray-200 shadow-sm rounded-2xl p-8 md:p-10" data-testid="login-card">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src="/logo.png" alt="Via Permuta" className="w-12 h-12 object-contain" />
            <div className="font-display font-bold text-xl">Via Permuta RP</div>
          </div>

          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-3" style={{ color: "#FF8A00" }}>
            Acesso ao sistema
          </div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-[#1F1F24] leading-tight" data-testid="login-title">
            Repositório de Senhas<br />Via Permuta RP
          </h1>
          <p className="text-gray-600 mt-3 text-sm leading-relaxed" data-testid="login-subtitle">
            Central segura para gerenciamento de credenciais, acessos e ativos digitais corporativos.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="seu@email.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 vp-btn-primary text-white font-semibold text-base rounded-lg"
              data-testid="login-submit-btn"
            >
              {loading ? "Entrando..." : "Entrar no repositório"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-xs text-gray-500 text-center">
            Sistema interno Via Permuta · Acesso restrito à equipe autorizada
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-md flex items-center justify-center"
           style={{ backgroundColor: "rgba(255,138,0,0.12)", color: "#FF8A00" }}>{icon}</div>
      <div>
        <div className="text-white text-sm font-semibold">{title}</div>
        <div className="text-gray-400 text-xs">{text}</div>
      </div>
    </div>
  );
}
